import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, ArrowUpDown, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerClose, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import api from "@/lib/api";
import CourseCard from "@/components/CourseCard";
import { useSEO } from "@/lib/seo";
import { trackEvent } from "@/lib/analytics";

const SORTS = [
  { label: "Popularity", value: "" },
  { label: "Duration: low to high", value: "duration_asc" },
  { label: "Duration: high to low", value: "duration_desc" },
  { label: "Cost: low to high", value: "cost_asc" },
  { label: "Cost: high to low", value: "cost_desc" },
];
const PROGRAM_TYPES = ["Certification", "Diploma", "Masters Track", "Master's Degree", "Bachelor's Degree", "Free Course"];

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [meta, setMeta] = useState({ countries: [], partners: [] });
  const [programs, setPrograms] = useState([]);
  const [freeCourses, setFreeCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);

  useSEO({
    title: "All Programs & Courses — Training Basket",
    description: "Browse IT certifications, diplomas and global university degrees. Filter by domain, skills, duration, country and budget.",
    canonical: `${window.location.origin}/courses`,
  });

  const filters = {
    q: params.get("q") || "",
    category: params.get("category") || params.get("domain") || "",
    program_type: params.get("type") || "",
    skill: params.get("skill") || "",
    country: params.get("country") || "",
    partner: params.get("partner") || "",
    pricing: params.get("pricing") || "",
    sort: params.get("sort") || "",
    biz: params.get("biz") || "",
  };

  const [searchInput, setSearchInput] = useState(filters.q);
  useEffect(() => { setSearchInput(filters.q); }, [filters.q]);
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput.trim() !== filters.q) setFilter("q", searchInput.trim());
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (filters.q) qs.set("q", filters.q);
      if (filters.category) qs.set("category", filters.category);
      if (filters.program_type) qs.set("program_type", filters.program_type);
      if (filters.skill) qs.set("skill", filters.skill);
      if (filters.country) qs.set("country", filters.country);
      if (filters.partner) qs.set("partner", filters.partner);
      if (filters.pricing) qs.set("pricing", filters.pricing);
      if (filters.biz) qs.set("workspace", filters.biz);
      if (filters.sort) qs.set("sort", filters.sort);
      const { data } = await api.get(`/programs?${qs.toString()}`);
      setPrograms(data.programs);
    } catch (e) {} finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => { trackEvent("page_view", { page: "catalog" }); load(); }, [load]);
  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data.categories)).catch(() => {});
    api.get("/programs/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/programs?free=true").then((r) => setFreeCourses(r.data.programs)).catch(() => {});
  }, []);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    ["category", "domain"].forEach((k) => next.delete(k));
    Object.entries({ category: filters.category, program_type: filters.program_type, skill: filters.skill, country: filters.country, partner: filters.partner, pricing: filters.pricing, sort: filters.sort, q: filters.q, biz: filters.biz, [key]: value })
      .forEach(([k, v]) => { if (v && v !== "all" && v !== "popularity") next.set(k, v); else next.delete(k); });
    if (key !== "category" && filters.category) next.set("category", filters.category);
    setParams(next);
  };

  const activeCat = categories.find((c) => c.slug === filters.category);
  const noFilters = !filters.q && !filters.category && !filters.program_type && !filters.skill && !filters.country && !filters.partner && !filters.pricing && !filters.biz;
  const activeCount = ["pricing", "category", "program_type", "country"].filter((k) => filters[k]).length;

  // Shared filter controls — rendered in the desktop sidebar (suffix "") and the mobile bottom sheet (suffix "-m").
  const renderFilterControls = (suffix = "") => (
    <>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Pricing</p>
        <Select value={filters.pricing || "all"} onValueChange={(v) => setFilter("pricing", v)}>
          <SelectTrigger className="rounded-xl" data-testid={`filter-pricing-select${suffix}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All pricing</SelectItem>
            {(meta.pricings && meta.pricings.length ? meta.pricings : ["Paid", "Free"]).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Domain</p>
        <Select value={filters.category || "all"} onValueChange={(v) => setFilter("category", v)}>
          <SelectTrigger className="rounded-xl" data-testid={`filter-domain-select${suffix}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All domains</SelectItem>
            {categories.filter((c) => (c.program_count || 0) > 0).map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Program type</p>
        <Select value={filters.program_type || "all"} onValueChange={(v) => setFilter("type", v)}>
          <SelectTrigger className="rounded-xl" data-testid={`filter-type-select${suffix}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {PROGRAM_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Country</p>
        <Select value={filters.country || "all"} onValueChange={(v) => setFilter("country", v)}>
          <SelectTrigger className="rounded-xl" data-testid={`filter-country-select${suffix}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All countries</SelectItem>
            {meta.countries.filter(Boolean).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="catalog-page">
      <p className="text-xs font-semibold uppercase tracking-widest text-rose-600">Programs & courses</p>
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-2" data-testid="catalog-title">
        {activeCat ? activeCat.name : filters.q ? `Results for "${filters.q}"` : "All programs"}
      </h1>
      <p className="text-slate-500 mt-2 text-sm">{loading && programs.length === 0 ? "Loading…" : `${programs.length} program${programs.length === 1 ? "" : "s"}${loading ? " · updating…" : ""}`}</p>

      {/* Mobile toolbar: compact Filters button + search */}
      <div className="flex items-center gap-2.5 mt-6 lg:hidden" data-testid="catalog-mobile-toolbar">
        <button onClick={() => setSheetOpen(true)}
          className="relative inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-900 shadow-2xs shrink-0 active:bg-slate-50 transition-colors"
          data-testid="catalog-filters-open">
          <SlidersHorizontal className="w-4 h-4" /> Filters
          {activeCount > 0 && (
            <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-[#17263B] text-white text-[10px] font-extrabold flex items-center justify-center" data-testid="catalog-filters-count">{activeCount}</span>
          )}
        </button>
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={searchInput} placeholder="Search programs…" className="pl-10 rounded-xl bg-white" data-testid="catalog-search-input-mobile"
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") setFilter("q", searchInput.trim()); }} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 mt-4 lg:mt-8">
        <aside className="hidden lg:block lg:col-span-1">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 flex flex-col gap-5 lg:sticky lg:top-24">
            <div className="flex items-center gap-2 text-slate-900 font-bold"><SlidersHorizontal className="w-4 h-4" /> Filters</div>
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={searchInput} placeholder="Search… (results update as you type)" className="pl-10 rounded-xl" data-testid="catalog-search-input"
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") setFilter("q", searchInput.trim()); }} />
            </div>
            {renderFilterControls("")}
            <Button variant="outline" className="rounded-xl" data-testid="filters-clear-button" onClick={() => setParams({})}>Clear all</Button>
          </div>
        </aside>

        <div className="lg:col-span-3">
          <div className="flex flex-wrap gap-2 mb-5" data-testid="catalog-biz-pills">
            {[["", "All programs"], ["retail", "Certifications"]].map(([k, l]) => (
              <button key={k} onClick={() => setFilter("biz", k)} data-testid={`catalog-biz-${k || "all"}`}
                className={`px-4 py-2 rounded-full text-xs font-bold border transition-colors ${filters.biz === k ? "bg-[#17263B] text-white border-[#17263B]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>{l}</button>
            ))}
          </div>
          {noFilters && freeCourses.length > 0 && (
            <div className="mb-8" data-testid="free-courses-section">
              <p className="text-xs font-semibold uppercase tracking-widest text-emerald-600 mb-3">Start free</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {freeCourses.map((p) => <CourseCard key={p.slug} program={p} />)}
              </div>
            </div>
          )}
          <div className="flex items-center justify-between mb-5">
            <p className="text-sm text-slate-500" data-testid="catalog-count">{programs.length} programs</p>
            <div className="flex items-center gap-2">
              <ArrowUpDown className="w-4 h-4 text-slate-400" />
              <Select value={filters.sort || "popularity"} onValueChange={(v) => setFilter("sort", v)}>
                <SelectTrigger className="w-52 rounded-xl bg-white" data-testid="sort-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SORTS.map((s) => <SelectItem key={s.value} value={s.value || "popularity"}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {loading && programs.length === 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">{[1, 2, 3].map((i) => <div key={i} className="h-80 rounded-2xl bg-slate-100 animate-pulse" />)}</div>
          ) : programs.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 p-14 text-center bg-white" data-testid="catalog-empty">
              <p className="font-semibold text-slate-700">No programs match these filters</p>
              <p className="text-sm text-slate-500 mt-1">Try clearing a filter or searching differently.</p>
            </div>
          ) : (
            <div className={`grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 transition-opacity ${loading ? "opacity-50 pointer-events-none" : ""}`} data-testid="catalog-results-grid">
              {programs.map((p) => <CourseCard key={p.slug} program={p} />)}
            </div>
          )}
        </div>
      </div>

      {/* Mobile filter bottom sheet */}
      <Drawer open={sheetOpen} onOpenChange={setSheetOpen}>
        <DrawerContent className="lg:hidden rounded-t-3xl max-h-[85vh] bg-white" data-testid="catalog-filter-sheet">
          <DrawerHeader className="border-b border-slate-100 px-5 py-4">
            <div className="flex items-center justify-between gap-4">
              <DrawerTitle className="text-lg font-extrabold text-slate-900">Filter programs</DrawerTitle>
              <DrawerClose asChild>
                <button className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50 shrink-0" data-testid="catalog-filter-sheet-close" aria-label="Close filters"><X className="w-4 h-4" /></button>
              </DrawerClose>
            </div>
          </DrawerHeader>
          <div className="overflow-y-auto px-5 py-5 flex flex-col gap-5">
            {renderFilterControls("-m")}
          </div>
          <div className="flex gap-3 border-t border-slate-100 px-5 py-4" style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}>
            <Button variant="outline" className="rounded-xl flex-1" data-testid="catalog-sheet-clear" onClick={() => setParams({})}>Clear all</Button>
            <Button className="rounded-xl flex-1 bg-[#FFC72C] hover:bg-[#F5B800] text-[#17263B] font-extrabold" data-testid="catalog-sheet-apply" onClick={() => setSheetOpen(false)}>
              Apply filters{programs.length ? ` (${programs.length})` : ""}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
